import { all, call, put, takeLatest } from '@redux-saga/core/effects';
import axios from 'axios';

import {
  requestViewActivityEngagement,
  requestViewHashtagEngagement,
  requestViewHashtagOccurrences,
  requestUploadAnswers
} from './administration.actions';
import {
  getViewActivityEngagementRequest,
  getViewHashtagEngagementRequest,
  getViewHashtagOccurencesRequest,
  getUploadAnswersRequest
} from './administration.endpoints';

import { addSystemNotice, addSpinner, removeSpinner } from '../../slices/system/system.slice';
import {
  setActivityEngagement,
  setHashtagEngagement,
  setHashtagOccurrences
} from '../../slices/programme/tribe/tribe.slice';

import { ERROR } from '../../utils/constants';
import { getErrorMessage } from '../../utils/api-util';

export function* performViewActivityEngagementRequest({ payload }) {
  const { programmeExternalReference, onSuccess } = payload;

  try {
    yield put(addSpinner('Retrieving activity engagement'));

    const [endpoint, options] = getViewActivityEngagementRequest(programmeExternalReference);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(setActivityEngagement(data.memberEngagements));
    yield put(removeSpinner('Retrieving activity engagement'));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(removeSpinner('Retrieving activity engagement'));
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForViewActivityEngagementRequest() {
  yield takeLatest(requestViewActivityEngagement.type, performViewActivityEngagementRequest);
}

export function* performViewHashtagEngagementRequest({ payload }) {
  const { programmeExternalReference, onSuccess } = payload;

  try {
    yield put(addSpinner('Retrieving hashtag engagement'));

    const [endpoint, options] = getViewHashtagEngagementRequest(programmeExternalReference);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(setHashtagEngagement(data.memberHashTags));
    yield put(removeSpinner('Retrieving hashtag engagement'));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(removeSpinner('Retrieving hashtag engagement'));
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForViewHashtagEngagementRequest() {
  yield takeLatest(requestViewHashtagEngagement.type, performViewHashtagEngagementRequest);
}

export function* performViewHashtagOccurrencesRequest({ payload }) {
  const { programmeExternalReference, onSuccess } = payload;

  try {
    const [endpoint, options] = getViewHashtagOccurencesRequest(programmeExternalReference);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(setHashtagOccurrences(data.hashTagOccurrences));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForViewHashtagOccurrencesRequest() {
  yield takeLatest(requestViewHashtagOccurrences.type, performViewHashtagOccurrencesRequest);
}

export function* performUploadAnswersRequest({ payload }) {
  const { input, onSuccess, onError } = payload;

  try {
    yield put(addSpinner('Uploading answers'));

    const [endpoint, options] = getUploadAnswersRequest(input);

    yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(removeSpinner('Uploading answers'));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(removeSpinner('Uploading answers'));
    yield put(addSystemNotice({ type: ERROR, message }));

    if (onError && error.response?.data?.failureString) {
      yield call(onError, error.response.data.failureString);
    }
  }
}

export function* watchForUploadAnswersRequest() {
  yield takeLatest(requestUploadAnswers.type, performUploadAnswersRequest);
}

export default function* administrationSaga() {
  yield all([
    watchForViewActivityEngagementRequest(),
    watchForViewHashtagEngagementRequest(),
    watchForViewHashtagOccurrencesRequest(),
    watchForUploadAnswersRequest()
  ]);
}
